// src/pages/GraduatesPage.jsx
// Past Prime Valor members who have gone on to compete professionally.
//
// Graduates are ordinary roster records in the "Graduates" division, managed
// from the admin panel like everyone else. The whole page is gated behind the
// graduates toggle in siteSettings/sections — until the team switches it on,
// visiting this URL just lands you back on the Teams page.

import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { usePlayersByGroup } from "../hooks/usePlayers";
import RosterSkeleton from "../components/RosterSkeleton";
import PlayerPhoto from "../components/PlayerPhoto";
import { subscribeToSectionSettings } from "../lib/siteSettings";

function GraduateCard({ grad }) {
  return (
    <Link
      to={`/roster/${grad.id}`}
      className="group bg-gray-900 border border-gray-800 rounded-lg overflow-hidden hover:border-red-600/50 transition-all duration-300 hover:shadow-lg hover:shadow-red-900/20 block"
    >
      {/* Photo */}
      <div className="aspect-square overflow-hidden bg-gray-800 relative">
        <PlayerPhoto
          player={grad}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-2 right-2 bg-red-600 text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded tracking-wider">
          Graduate
        </div>
      </div>

      {/* Info */}
      <div className="p-4">
        {grad.specialization && (
          <span className="text-red-500 text-xs font-semibold uppercase tracking-wider">
            {grad.specialization}
          </span>
        )}
        <h3 className="text-white font-bold text-lg mt-1 group-hover:text-red-400 transition-colors">
          {grad.name}
        </h3>
        {grad.country && <p className="text-gray-500 text-sm mt-1">{grad.country}</p>}
        {grad.currentTeam && (
          <p className="text-gray-400 text-xs mt-2 uppercase tracking-wide">
            Now with {grad.currentTeam}
          </p>
        )}
      </div>
    </Link>
  );
}

function GraduatesPage() {
  const [sections, setSections] = useState(null); // null while loading
  useEffect(() => subscribeToSectionSettings(setSections), []);

  const { players: graduates, loading } = usePlayersByGroup("graduates");

  // Hold a blank screen until we know whether the section is switched on —
  // otherwise anyone with the URL gets a flash of the hidden page first.
  if (sections === null) {
    return <main className="bg-black min-h-screen" />;
  }
  // Section switched off — don't render a hidden page to anyone with the URL.
  if (!sections.graduatesEnabled) {
    return <Navigate to="/roster" replace />;
  }

  return (
    <main className="bg-black min-h-screen">
      {/* Hero Section — gradient (no division image yet) */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-red-950/40 to-black">
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `linear-gradient(rgba(220,38,38,0.3) 1px, transparent 1px),
                                linear-gradient(90deg, rgba(220,38,38,0.3) 1px, transparent 1px)`,
              backgroundSize: "30px 30px",
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
        </div>
        <div className="relative z-10 text-center px-6">
          <span className="text-red-500 text-sm font-semibold uppercase tracking-wider mb-2 block">
            Gone Pro
          </span>
          <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tight text-white mb-4">
            <span className="text-red-600">Graduates</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Past Prime Valor members now competing on the professional stage
          </p>
          <Link
            to="/roster"
            className="inline-flex items-center gap-2 mt-6 text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Teams
          </Link>
        </div>
      </section>

      {/* Graduates Grid */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 uppercase border-l-4 border-red-600 pl-4">
            Prime Valor Graduates
          </h2>

          {loading ? (
            <RosterSkeleton count={4} />
          ) : graduates.length === 0 ? (
            <p className="text-gray-500 text-center py-16">
              No graduates announced yet — watch this space.
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {graduates.map((grad) => (
                <GraduateCard key={grad.id} grad={grad} />
              ))}
            </div>
          )}

          <p className="text-gray-600 text-sm mt-12 text-center max-w-2xl mx-auto">
            From the Caribbean to the world stage — these players started their journey with
            Prime Valor and earned their shot at the professional level.
          </p>
        </div>
      </section>
    </main>
  );
}

export default GraduatesPage;
